import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { workoutsAPI } from '../api';
import { shoesAPI } from '../api';
import { useTranslation } from '../i18n';
import { addDays, startOfWeek, format, isSameDay, parseISO } from 'date-fns';
import WorkoutDetail from './WorkoutDetail';
import { formatPace } from "../utils/formatPace";
import { formatDuration } from "../utils/formatDuration";
import DowngradeModal from '../components/journal/DowngradeModal';
import { toast } from '../components/common/Notifier';


function getWeekStart(date) {
  return startOfWeek(date, { weekStartsOn: 1 }); // понедельник
}

const getTagLabel = (tag, t) => {
  return `⚠️ ${t(`compliance.tags.${tag}`)}`;
};

export const WorkoutsPage = () => {
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [downgradeWorkout, setDowngradeWorkout] = useState(null);
  const deepLinkDateRef = useRef(searchParams.get('date'));

  const [weekStart, setWeekStart] = useState(() =>
      getWeekStart(deepLinkDateRef.current ? parseISO(deepLinkDateRef.current) : new Date())
  );
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [shoes, setShoes] = useState([]);

  const handleDowngrade = async (workoutId, payload) => {
    try {
      await workoutsAPI.downgrade(workoutId, payload);
      toast.success(t('downgrade.success_message') || 'Тренировка успешно заменена на Easy');
      loadWeek();
    } catch (e) {
      toast.error(t('error.general'));
    }
  };

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const {data} = await workoutsAPI.list({
        week_start: format(weekStart, 'yyyy-MM-dd'),
      });
      setWorkouts(Array.isArray(data) ? data : data?.results || []);
    } catch (e) {
      setWorkouts([]);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    shoesAPI.list().then(({ data }) => setShoes((data.results ?? data).filter((s) => !s.retired_at)));
  }, []);

  useEffect(() => {
    loadWeek();
  }, [loadWeek]);

  useEffect(() => {
    if (!deepLinkDateRef.current || workouts.length === 0) return;
    const match = workouts.find((w) => w.scheduled_date === deepLinkDateRef.current);
    if (match) setSelectedId(match.id);
    deepLinkDateRef.current = null;
    setSearchParams({}, {replace: true});
  }, [workouts, setSearchParams]);

  const days = Array.from({length: 7}, (_, i) => addDays(weekStart, i));

  const bucketFor = (type) => {
    if (type === 'threshold' || type === 'long_run') return 'threshold';
    if (type === 'race_pace_intervals' || type === 'race') return 'race_pace';
    if (type === 'short_reps' || type === 'hills' || type === 'prerace_intervals') return 'reps';
    if (type === 'easy') return 'easy';
    return 'other';
  };

  const handleShoeChange = async (workout, shoeId) => {
    try {
      await workoutsAPI.setShoe(workout.id, shoeId || null);
      setWorkouts((prev) => prev.map((w) => w.id === workout.id ? { ...w, shoe: shoeId ? Number(shoeId) : null } : w));
    } catch { toast.error(t('error.general')); }
  };

  const weeklySummary = useMemo(() => {
    const totals = {threshold: 0, race_pace: 0, reps: 0, easy: 0, other: 0};
    let totalLoad = 0;
    let totalScoreSum = 0;
    let scoredDaysCount = 0;
    let plannedTotalKm = 0;

    workouts.forEach((w) => {
      plannedTotalKm += (w.distance_km || 0);

      if (w.compliance?.recovery_cost) {
        totalLoad += w.compliance.recovery_cost;
      } else if (w.recovery_cost) {
        totalLoad += w.recovery_cost;
      }

      if (w.compliance?.overall_score != null) {
        totalScoreSum += w.compliance.overall_score;
        scoredDaysCount += 1;
      }

      if (!w.is_completed) return;

      const hasIntervalData = (w.work_km || 0) > 0 || (w.supporting_km || 0) > 0;

      if (hasIntervalData) {
        const bucket = bucketFor(w.workout_type);
        totals[bucket] += w.work_km || 0;
        totals.easy += w.supporting_km || 0;
      } else {
        const bucket = bucketFor(w.workout_type);
        totals[bucket] += w.actual_distance_km || 0;
      }

      (w.supplementary_activities || []).forEach((act) => {
        const actBucket = bucketFor(act.activity_type);
        totals[actBucket] += act.distance_km || 0;
      });
    });

    const total = Object.values(totals).reduce((sum, v) => sum + v, 0);
    const quality = totals.threshold + totals.race_pace + totals.reps;
    const qualityPct = total > 0 ? Math.round((quality / total) * 1000) / 10 : 0;
    const avgScore = scoredDaysCount > 0 ? Math.round(totalScoreSum / scoredDaysCount) : null;
    const planCompletionPct = plannedTotalKm > 0 ? Math.round(Math.min(total / plannedTotalKm, 1.0) * 100) : null;

    return {
      total: Math.round(total * 10) / 10,
      plannedTotalKm: Math.round(plannedTotalKm * 10) / 10,
      qualityPct,
      threshold: Math.round(totals.threshold * 10) / 10,
      racePace: Math.round(totals.race_pace * 10) / 10,
      reps: Math.round(totals.reps * 10) / 10,
      easy: Math.round(totals.easy * 10) / 10,
      totalLoad: Math.round(totalLoad),
      avgScore,
      planCompletionPct,
    };
  }, [workouts]);

  const workoutByDate = Object.fromEntries(
      workouts.map((w) => [w.scheduled_date, w])
  );

  const isCurrentWeek = isSameDay(weekStart, getWeekStart(new Date()));

  const getVerdictBadge = (verdict) => {
    switch (verdict) {
      case 'excellent':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'acceptable':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'poor':
      default:
        return 'bg-rose-100 text-rose-800 border-rose-200';
    }
  };

  if (selectedId) {
    return (
        <WorkoutDetail
            workoutId={selectedId}
            onBack={() => {
              setSelectedId(null);
              loadWeek();
            }}
        />
    );
  }

  return (
      <div className="max-w-6xl mx-auto space-y-6">
        {/* ── ХЕДЕР СТРАНИЦЫ И НАВИГАЦИЯ ── */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">{t('workouts.title')}</h1>
          <div className="flex items-center gap-2">
            <button
                onClick={() => setWeekStart((d) => addDays(d, -7))}
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium transition-colors"
            >
              ← {t('workouts.prev_week')}
            </button>
            {!isCurrentWeek && (
                <button
                    onClick={() => setWeekStart(getWeekStart(new Date()))}
                    className="px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-medium transition-colors"
                >
                  {t('dashboard.today')}
                </button>
            )}
            <span className="text-sm font-medium text-gray-600 px-2">
          {format(weekStart, 'dd.MM')} – {format(addDays(weekStart, 6), 'dd.MM')}
        </span>
            <button
                onClick={() => setWeekStart((d) => addDays(d, 7))}
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium transition-colors"
            >
              {t('workouts.next_week')} →
            </button>
          </div>
        </div>

        {loading ? (
            <p className="text-gray-400 py-8 text-center">{t('common.loading')}</p>
        ) : (
            <div className="space-y-4">
              {/* ── СЕТКА ДНЕЙ НЕДЕЛИ ── */}
              <div className="grid grid-cols-1 gap-4">
                {days.map((day) => {
                  const key = format(day, 'yyyy-MM-dd');
                  const workout = workoutByDate[key];
                  const comp = workout?.compliance;

                  return (
                      <div
                          key={key}
                          onClick={() => workout && setSelectedId(workout.id)}
                          className={`bg-white rounded-xl shadow border border-gray-100 p-4 transition-all ${
                              workout ? 'cursor-pointer hover:shadow-md hover:border-blue-200' : 'opacity-60 bg-gray-50'
                          }`}
                      >
                        {/* Заголовок дня */}
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3">
                          <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">
                      {format(day, 'EEEE, dd.MM')}
                    </span>
                            {workout && (
                                <span
                                    className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold uppercase">
                        {t(`${workout.workout_type}`)}
                      </span>
                            )}
                          </div>

                          <div>
                            {workout?.is_completed ? (
                                <span
                                    className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                        ✓ {t('workouts.completed')}
                      </span>
                            ) : workout ? (
                                <div className="flex items-center gap-2">
                                  {workout.workout_type !== 'easy' && workout.workout_type !== 'recovery' && (
                                      <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDowngradeWorkout(workout);
                                          }}
                                          className="text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full font-medium transition-all shadow-xs hover:scale-105 active:scale-95 flex items-center gap-1"
                                          title={t('downgrade.button_hint')}
                                      >
                                        <span>⬇️</span>
                                        <span>{t('downgrade.button')}</span>
                                      </button>
                                  )}
                                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                          {t('workouts.planned')}
                        </span>
                                </div>
                            ) : (
                                <span className="text-xs text-gray-400">{t('workouts.rest_day')}</span>
                            )}
                          </div>
                        </div>

            {workout ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ЛЕВАЯ КОЛОНКА */}
                  <div className="space-y-2 border-r-0 md:border-r md:pr-4 border-gray-100">
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="text-gray-400">{t('workouts.distance')}:</span>
                      <span className="font-semibold text-gray-800">
              {workout.actual_distance_km ? `${workout.actual_distance_km} ${t('common.km')}` : '—'}
                        {workout.distance_km != null && (
                            <span className="text-gray-400 font-normal ml-1">
                  ({t('workouts.plan')}: {workout.distance_km} {t('common.km')})
                </span>
                        )}
            </span>
                    </div>

                    <div className="flex items-baseline justify-between text-xs">
                      <span className="text-gray-400">{t('workouts.pace')} / GAP:</span>
                      <span className="font-semibold text-gray-800">
              {workout.actual_avg_pace_min_per_km ? `${formatPace(workout.actual_avg_pace_min_per_km)}` : '—'}
                        {workout.actual_gap_min_per_km && (
                            <span className="text-purple-600 ml-1 font-medium">
                  (GAP: {formatPace(workout.actual_gap_min_per_km)})
                </span>
                        )}
            </span>
                    </div>

                    <div className="flex items-baseline justify-between text-xs">
                      <span
                          className="text-gray-400">{t('workouts.time')} / {t('workouts.elevation')}:</span>
                      <span className="font-semibold text-gray-800">
              {workout.actual_duration_sec ? formatDuration(workout.actual_duration_sec) : '—'}
                        {workout.actual_elevation_gain_m != null && (
                            <span className="text-gray-500 font-normal ml-1">
                  · ↗ {Math.round(workout.actual_elevation_gain_m)} {t('common.m')}
                </span>
                        )}
            </span>
                    </div>
                  {workout.is_completed && (() => {
                    const selected = shoes.find((s) => s.id === workout.shoe);
                    return (
                      <div className="flex items-center justify-between gap-3 text-xs" onClick={(e) => e.stopPropagation()}>
                        <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                          {selected?.photo_url
                            ? <img src={selected.photo_url} alt="" className="w-full h-full object-cover" />
                            : <span className="text-xl text-gray-400">👟</span>}
                        </div>
                        <select
                          value={workout.shoe || ''}
                          onChange={(e) => handleShoeChange(workout, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1 max-w-[60%]"
                        >
                          <option value="">—</option>
                          {shoes.map((s) => (
                            <option key={s.id} value={s.id}>{s.nickname || `${s.brand} ${s.model}`}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}

                    {workout.notes && workout.notes.length > 0 && (
                        <p className="text-xs text-gray-500 italic bg-gray-50 p-2 rounded">
                          {Array.isArray(workout.notes) ? workout.notes.join(' · ') : workout.notes}
                        </p>
                    )}

                    {workout.supplementary_activities && workout.supplementary_activities.length > 0 && (
                        <div className="pt-1 space-y-1">
                          {workout.supplementary_activities.map((act) => (
                              <div key={act.id}
                                   className="text-[11px] text-amber-800 bg-amber-50/80 border border-amber-200/60 rounded px-2 py-0.5 flex justify-between items-center">
                                <span>+ {t('workouts.supplementary_activity')}</span>
                                <span className="font-medium">
                    {act.distance_km} {t('common.km')} {act.duration_sec ? `(${formatDuration(act.duration_sec)})` : ''}
                  </span>
                              </div>
                          ))}
                        </div>
                    )}
                  </div>

                              {/* ПРАВАЯ КОЛОНКА */}
                              <div
                                  className="flex flex-col justify-between bg-gray-50/70 rounded-lg p-3 border border-gray-100">
                                {comp ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-black text-gray-900 leading-none">
                                {comp.overall_score}%
                              </span>
                                          <span className="text-[10px] uppercase font-bold text-gray-400">Score</span>
                                        </div>
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded font-bold border ${getVerdictBadge(comp.verdict)}`}>
                              {comp.verdict.toUpperCase()}
                            </span>
                                      </div>

                                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs pt-1">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">{t('compliance.completion')}:</span>
                                          <span className="font-bold text-gray-800">{comp.completion_pct}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">{t('compliance.execution')}:</span>
                                          <span
                                              className="font-bold text-gray-800">{comp.execution_score != null ? `${comp.execution_score}%` : '—'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500">{t('compliance.hr_control')}:</span>
                                          <span
                                              className="font-bold text-gray-800">{comp.hr_control_score != null ? `${comp.hr_control_score}%` : '—'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                              <span className="text-gray-500 flex items-center gap-0.5">
                                {t('compliance.recovery_cost')}: ⚡
                              </span>
                                          <span className="font-bold text-gray-900">{comp.recovery_cost}</span>
                                        </div>
                                      </div>

                                      {comp.tags && comp.tags.length > 0 && (
                                          <div className="flex flex-wrap gap-1 pt-1">
                                            {comp.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="text-[10px] bg-amber-100 text-amber-900 border border-amber-200/80 px-1.5 py-0.5 rounded font-medium shadow-xs"
                                                >
                                  {getTagLabel(tag, t)}
                                </span>
                                            ))}
                                          </div>
                                      )}
                                    </div>
                                ) : (
                                    <div
                                        className="h-full flex items-center justify-center text-xs text-gray-400 italic py-3 text-center">
                                      {workout.is_completed
                                        ? (workout.source === 'manual' && workout.distance_km == null
                                            ? t('workouts.unplanned')
                                            : t('compliance.evaluating'))
                                        : t('compliance.not_completed_yet')}
                                    </div>
                                )}
                              </div>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 py-2">{t('workouts.no_plan')}</p>
                        )}
                      </div>
                  );
                })}
              </div>

              {/* ── БЛОК ИТОГОВ НЕДЕЛИ ── */}
              <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-xl shadow-lg p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* 1. Объём и качество */}
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wider text-blue-200 font-semibold">
                      {t('dashboard.weekly_total')}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black">{weeklySummary.total}</span>
                      <span className="text-sm text-blue-200">
                  / {weeklySummary.plannedTotalKm} {t('common.km')}
                </span>
                    </div>
                    <p className="text-xs text-blue-200">
                      {t('dashboard.quality_share')}: <b className="text-white">{weeklySummary.qualityPct}%</b>
                    </p>
                  </div>

                  {/* 2. Общая нагрузка недели */}
                  <div className="space-y-1 border-t md:border-t-0 md:border-l border-blue-700/50 md:pl-6 pt-3 md:pt-0">
                    <p className="text-xs uppercase tracking-wider text-blue-200 font-semibold flex items-center gap-1">
                      {t('dashboard.weekly_load')} ⚡
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black text-amber-300">{weeklySummary.totalLoad}</span>
                      <span className="text-xs text-blue-200">{t('common.pts')}</span>
                    </div>
                    <p className="text-xs text-blue-200">
                      {weeklySummary.totalLoad < 1500
                          ? t('dashboard.load_recovery')
                          : weeklySummary.totalLoad < 2600
                              ? t('dashboard.load_optimal')
                              : t('dashboard.load_high')}
                    </p>
                  </div>

                  {/* 3. Итоговое выполнение плана недели */}
                  <div className="space-y-1 border-t md:border-t-0 md:border-l border-blue-700/50 md:pl-6 pt-3 md:pt-0">
                    <p className="text-xs uppercase tracking-wider text-blue-200 font-semibold">
                      {t('dashboard.weekly_compliance')}
                    </p>
                    <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-emerald-400">
                  {weeklySummary.avgScore != null ? `${weeklySummary.avgScore}%` : '—'}
                </span>
                      <span className="text-xs text-blue-200">{t('dashboard.avg_score')}</span>
                    </div>
                    <p className="text-xs text-blue-200">
                      {t('dashboard.plan_volume')}: <b
                        className="text-white">{weeklySummary.planCompletionPct || 0}%</b>
                    </p>
                  </div>
                </div>

                {/* Раскладка по зонам */}
                <div className="mt-4 pt-3 border-t border-blue-800/80 flex flex-wrap gap-4 text-xs text-blue-200">
                  <span>{t('workouts.threshold')}: <b
                      className="text-white">{weeklySummary.threshold} {t('common.km')}</b></span>
                  <span>{t('workouts.race_pace')}: <b
                      className="text-white">{weeklySummary.racePace} {t('common.km')}</b></span>
                  <span>{t('workouts.reps')}: <b className="text-white">{weeklySummary.reps} {t('common.km')}</b></span>
                  <span>{t('workouts.easy')}: <b className="text-white">{weeklySummary.easy} {t('common.km')}</b></span>
                </div>
              </div>
            </div>
        )}

        {/* Модалка понижения тренировки */}
        <DowngradeModal
            workout={downgradeWorkout}
            isOpen={Boolean(downgradeWorkout)}
            onClose={() => setDowngradeWorkout(null)}
            onConfirm={handleDowngrade}
        />
      </div>
  );
}